import { Css, MenuItem, useModal } from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { ReadyPlanLikeButtonMenuFragment } from "src/components/ReadyPlanButtonMenu.generated";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { useConfirmDiscardDraftVersionModal_ConfirmActionMutation } from "src/routes/libraries/plan-package/details/components/ConfirmDiscardDraftVersionModal.generated";
type RedirectToFn = (readyPlanLikeId: string, versionId: string) => string;
type ConfirmDiscardDraftVersionModalProps = { packageData: ReadyPlanLikeButtonMenuFragment; redirectTo?: RedirectToFn };

/**
 * Designed to provide a unified confirm discard modal for PP/DP/POF.
 */
export function ConfirmDiscardDraftVersionModal({ packageData, redirectTo }: ConfirmDiscardDraftVersionModalProps) {
  const [discardDraft] = useConfirmDiscardDraftVersionModal_ConfirmActionMutation();
  const { closeModal } = useModal();
  const navigate = useNavigate();

  const onConfirm = useCallback(async () => {
    const { data } = await discardDraft({ variables: { rpavId: packageData.aggregateDraft!.id } });
    closeModal();
    if (redirectTo && data && data.deleteReadyPlanAggregateVersion.latestReadyPlanAggregateVersion) {
      navigate(redirectTo(packageData.id, data.deleteReadyPlanAggregateVersion.latestReadyPlanAggregateVersion.id));
    }
  }, [discardDraft, redirectTo, navigate, packageData, closeModal]);

  return (
    <ConfirmationModal
      title="Discard Draft"
      label="Yes, Discard"
      onConfirmAction={onConfirm}
      confirmationMessage={
        <>
          Are you sure you want to discard progress on{" "}
          <span css={Css.fw6.$}>{packageData.aggregateDraft!.displayVersion}</span> of{" "}
          <span css={Css.fw6.$}>{packageData.name}</span>?
        </>
      }
    />
  );
}

export function useConfirmDiscardDraftVersionModalButtonMenuItem(
  packageData: ReadyPlanLikeButtonMenuFragment,
  redirectTo?: RedirectToFn,
): MenuItem {
  const { openModal } = useModal();
  const draftRpav = packageData?.aggregateDraft;
  const menuItem = useMemo(
    () => ({
      label: "Discard Draft",
      ...(packageData && draftRpav
        ? {
            onClick: () =>
              openModal({
                content: <ConfirmDiscardDraftVersionModal packageData={packageData} redirectTo={redirectTo} />,
              }),
            disabled: disableBasedOnPotentialOperation(draftRpav.canDelete),
          }
        : { onClick: () => {}, disabled: !packageData ? "Loading..." : "No draft to discard" }),
    }),
    [packageData, draftRpav, openModal, redirectTo],
  );

  return menuItem;
}
